import * as THREE from 'three';
import { mmToMeshUnits } from './loadCortexMesh';

// Approximate adult cerebellar dimensions, in millimetres: roughly 105 mm wide,
// 52 mm tall, 62 mm deep. Specified in real units and converted at the same
// scale as the cortical surface mesh, so the two stay proportionate.
const HALF_WIDTH = mmToMeshUnits(52.5);
const HALF_HEIGHT = mmToMeshUnits(26);
const HALF_DEPTH = mmToMeshUnits(31);

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * Builds the cerebellum: distinctly wider than it is tall, with a flattened
 * superior surface (it sits beneath the tentorium, under the occipital lobe),
 * a raised midline vermis, and the fine parallel folia that visually
 * distinguish cerebellar cortex from the much coarser cerebral gyri.
 */
export function createCerebellumGeometry(): THREE.BufferGeometry {
  const icosahedron = new THREE.IcosahedronGeometry(1, 5);
  const positions = icosahedron.attributes.position;
  const vertexCount = positions.count;
  const deformed = new Float32Array(vertexCount * 3);

  for (let i = 0; i < vertexCount; i++) {
    const dx = positions.getX(i);
    const dy = positions.getY(i);
    const dz = positions.getZ(i);

    // Wider than tall, and compact enough to tuck beneath the occipital lobe
    // rather than protruding behind it.
    const px = dx * HALF_WIDTH;
    const py = dy * HALF_HEIGHT;
    const pz = dz * HALF_DEPTH;

    const baseRadius = Math.sqrt(px * px + py * py + pz * pz);
    if (baseRadius < 1e-6) {
      deformed[i * 3] = 0;
      deformed[i * 3 + 1] = 0;
      deformed[i * 3 + 2] = 0;
      continue;
    }

    let dr = 0;

    // Flatten the superior surface against the tentorium.
    dr -= mmToMeshUnits(6) * smoothstep(0.3, 1.0, dy);

    // Fine, roughly horizontal parallel folia — much finer than cerebral gyri.
    // Folia are ~1 mm apart in reality, which is far too fine to render; these
    // are exaggerated to a visible spacing while staying clearly finer than the
    // cortical gyri on the surrounding cerebrum.
    dr += Math.sin(py * 105 + pz * 6) * mmToMeshUnits(1.3);
    dr += Math.sin(py * 165) * mmToMeshUnits(0.6);

    // The vermis is a raised midline ridge, flanked by a shallow groove on
    // each side where it meets the hemispheres.
    const vermisWidth = mmToMeshUnits(6);
    dr += mmToMeshUnits(2.4) * Math.exp(-((px / vermisWidth) ** 2));
    dr -= mmToMeshUnits(1.3) * Math.exp(-(((Math.abs(px) - mmToMeshUnits(13)) / vermisWidth) ** 2));

    const scale = (baseRadius + dr) / baseRadius;
    deformed[i * 3] = px * scale;
    deformed[i * 3 + 1] = py * scale;
    deformed[i * 3 + 2] = pz * scale;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(deformed, 3));
  geometry.setIndex(icosahedron.index);
  geometry.computeVertexNormals();
  icosahedron.dispose();

  return geometry;
}
